import React from 'react'; import { motion } from 'framer-motion'; import { useNavigate } from 'react-router-dom'; import { useTestStore } from '../../store/testStore'; import Button from '../common/Button'; import { ArrowRight, CheckCircle } from 'lucide-react';
const UserDetailsForm: React.FC = () => {
const navigate = useNavigate();
const { setUserDetails, userDetails } = useTestStore();
const [formData, setFormData] = React.useState({
fullName: userDetails?.fullName || '',
businessType: userDetails?.businessType || '',
role: userDetails?.role || '',
country: userDetails?.country || '',
state: userDetails?.state || '',
email: userDetails?.email || '',
phone: userDetails?.phone || ''
});
const [errors, setErrors] = React.useState<Record<string, string>>({});
const [isSubmitting, setIsSubmitting] = React.useState(false);

const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
const { name, value } = e.target;
setFormData(prev => ({ ...prev, [name]: value }));
if (errors[name]) {
setErrors(prev => ({ ...prev, [name]: '' }));
}
};

const validateForm = () => {
const newErrors: Record<string, string> = {};
if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
if (!formData.businessType.trim()) newErrors.businessType = 'Business type is required';
if (!formData.role.trim()) newErrors.role = 'Role is required';
if (!formData.country.trim()) newErrors.country = 'Country is required';
if (!formData.state.trim()) newErrors.state = 'State is required';
if (!formData.email.trim()) newErrors.email = 'Email is required';
else if (!/\S+@\S+.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';


setErrors(newErrors);
return Object.keys(newErrors).length === 0;
};

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!validateForm()) return;

  setIsSubmitting(true);

  try {
    // Save user details and wait for the result
    const userId = await setUserDetails(formData);
    console.log('User details saved successfully with ID:', userId);
    
    // Navigate to landing page after successful submission
    navigate('/');
  } catch (error) {
    console.error('Error saving user details:', error);
    // Show error message to user
    setErrors(prev => ({
      ...prev,
      submit: 'Failed to save your details. Please try again.'
    }));
  } finally {
    setIsSubmitting(false);
  }
};

const inputClasses = "w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 bg-white";
const labelClasses = "block text-sm font-semibold text-gray-700 mb-2";
const errorClasses = "text-error-600 text-sm mt-1 flex items-center gap-1";

return (
  <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4">
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="w-full max-w-4xl"
    >
      {/* Header Section */}
      <div className="text-center mb-12">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="flex items-center justify-center mb-6"
        >
          <div className="bg-primary-100 p-4 rounded-full">
            <CheckCircle className="h-12 w-12 text-primary-600" />
          </div>
        </motion.div>
        
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
        >
          Export Readiness Test
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-xl text-gray-600 mb-2"
        >
          Evaluate Your Business's Export Potential
        </motion.p>
        
        <motion.p
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="text-lg text-gray-500"
        >
          Powered by Lagos School of Export Management
        </motion.p>
      </div>

      {/* Form Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        className="bg-white rounded-2xl shadow-2xl p-8 md:p-12"
      >
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
          Let's Get Started
        </h2>
        <p className="text-gray-600 text-lg">
          Please provide some information about yourself and your business to personalize your export readiness assessment.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.7, duration: 0.4 }}
          >
            <label htmlFor="fullName" className={labelClasses}>Full Name *</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Enter your full name"
              className={`${inputClasses} ${errors.fullName ? 'border-error-500 focus:border-error-500 focus:ring-error-200' : ''}`}
              disabled={isSubmitting}
            />
            {errors.fullName && (
              <p className={errorClasses}>
                <span className="text-error-500">⚠</span>
                {errors.fullName}
              </p>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.8, duration: 0.4 }}
          >
            <label htmlFor="email" className={labelClasses}>Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@company.com"
              className={`${inputClasses} ${errors.email ? 'border-error-500 focus:border-error-500 focus:ring-error-200' : ''}`}
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className={errorClasses}>
                <span className="text-error-500">⚠</span>
                {errors.email}
              </p>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.9, duration: 0.4 }}
          >
            <label htmlFor="businessType" className={labelClasses}>Company *</label>
            <input
              type="text"
              id="businessType"
              name="businessType"
              value={formData.businessType}
              onChange={handleChange}
              placeholder="e.g., XYZ Export Ltd.,"
              className={`${inputClasses} ${errors.businessType ? 'border-error-500 focus:border-error-500 focus:ring-error-200' : ''}`}
              disabled={isSubmitting}
            />
            {errors.businessType && (
              <p className={errorClasses}>
                <span className="text-error-500">⚠</span>
                {errors.businessType}
              </p>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.0, duration: 0.4 }}
          >
            <label htmlFor="role" className={labelClasses}>Your Role *</label>
            <input
              type="text"
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              placeholder="e.g., CEO, Export Manager, Business Owner"
              className={`${inputClasses} ${errors.role ? 'border-error-500 focus:border-error-500 focus:ring-error-200' : ''}`}
              disabled={isSubmitting}
            />
            {errors.role && (
              <p className={errorClasses}>
                <span className="text-error-500">⚠</span>
                {errors.role}
              </p>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.1, duration: 0.4 }}
          >
            <label htmlFor="country" className={labelClasses}>Country *</label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              placeholder="Enter your country"
              className={`${inputClasses} ${errors.country ? 'border-error-500 focus:border-error-500 focus:ring-error-200' : ''}`}
              disabled={isSubmitting}
            />
            {errors.country && (
              <p className={errorClasses}>
                <span className="text-error-500">⚠</span>
                {errors.country}
              </p>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.2, duration: 0.4 }}
          >
            <label htmlFor="state" className={labelClasses}>State/Province *</label>
            <input
              type="text"
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="Enter your state or province"
              className={`${inputClasses} ${errors.state ? 'border-error-500 focus:border-error-500 focus:ring-error-200' : ''}`}
              disabled={isSubmitting}
            />
            {errors.state && (
              <p className={errorClasses}>
                <span className="text-error-500">⚠</span>
                {errors.state}
              </p>
            )}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.4 }}
        >
          <label htmlFor="phone" className={labelClasses}>Phone Number *</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+234 (805) 123-4567"
            className={`${inputClasses} ${errors.phone ? 'border-error-500 focus:border-error-500 focus:ring-error-200' : ''}`}
            disabled={isSubmitting}
          />
          {errors.phone && (
            <p className={errorClasses}>
              <span className="text-error-500">⚠</span>
              {errors.phone}
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.4 }}
          className="bg-primary-50 rounded-xl p-6 border border-primary-100"
        >
          <h3 className="font-semibold text-primary-800 mb-2">What to Expect</h3>
          <ul className="text-sm text-primary-700 space-y-1">
            <li>• Comprehensive 60-minute assessment across 10 key export categories</li>
            <li>• 200 targeted questions designed by export professionals</li>
            <li>• Detailed results with personalized recommendations</li>
            <li>• Professional PDF report for your records</li>
          </ul>
        </motion.div>

        <motion.div 
          className="flex justify-center pt-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.4 }}
        >
          <Button
            type="submit"
            variant="primary"
            size="lg"
            rightIcon={<ArrowRight />}
            disabled={isSubmitting}
            className="px-12 py-4 text-lg font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200"
          >
            {isSubmitting ? 'Saving Details...' : 'Begin Assessment'}
          </Button>
        </motion.div>
      </form>
    </motion.div>

    {/* Footer Note */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.6, duration: 0.4 }}
      className="text-center mt-8 text-gray-500"
    >
      <p className="text-sm">
        Your information is secure and will only be used to personalize your assessment results.
      </p>
    </motion.div>

    {/* Add error message display after the form */}
    {errors.submit && (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-error-600 text-center mt-4 p-3 bg-error-50 rounded-lg"
      >
        {errors.submit}
      </motion.div>
    )}
  </motion.div>
</div>
);
};

export default UserDetailsForm;